import { useCallback, useRef, useState } from "react";

import { use2DNavigation } from "./use2DNavigation";
import { useCellMode, hasInteractiveElements } from "./useCellMode";

export interface UseSelectionArgs {
    itemCount: number;
    getKeyByIndex: (index: number) => string;
    onActivate: (key: string) => void;
    isDisabledAt?: (index: number) => boolean;
    hasSelection: boolean;
    listId: string;
    columns?: number;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Find the next enabled index in a given direction
 * Returns -1 if no valid index found (no wrapping per WAI-ARIA Grid spec)
 */
const findNextEnabledIndex = (
    currentIndex: number,
    direction: 1 | -1,
    itemCount: number,
    isDisabledAt: (index: number) => boolean,
): number => {
    if (itemCount === 0) {
        return -1;
    }

    let nextIndex = currentIndex + direction;

    // Stop at boundaries (no wrapping)
    if (nextIndex < 0 || nextIndex >= itemCount) {
        return -1;
    }

    // Find next enabled item within bounds
    let attempts = 0;
    while (attempts < itemCount && nextIndex >= 0 && nextIndex < itemCount && isDisabledAt(nextIndex)) {
        nextIndex += direction;
        attempts++;
    }

    return nextIndex >= 0 && nextIndex < itemCount && !isDisabledAt(nextIndex) ? nextIndex : -1;
};

/**
 * Find first enabled index in the entire list
 */
const findFirstEnabledIndex = (itemCount: number, isDisabledAt: (index: number) => boolean): number => {
    for (let i = 0; i < itemCount; i++) {
        if (!isDisabledAt(i)) {
            return i;
        }
    }
    return -1;
};

/**
 * Find last enabled index in the entire list
 */
const findLastEnabledIndex = (itemCount: number, isDisabledAt: (index: number) => boolean): number => {
    for (let i = itemCount - 1; i >= 0; i--) {
        if (!isDisabledAt(i)) {
            return i;
        }
    }
    return -1;
};

/**
 * Find first/last enabled index in a specific row
 */
const findInRow = (
    currentIndex: number,
    columns: number,
    itemCount: number,
    isDisabledAt: (index: number) => boolean,
    direction: "first" | "last",
): number => {
    const currentRow = Math.floor(currentIndex / columns);
    const firstInRow = currentRow * columns;
    const lastInRow = Math.min((currentRow + 1) * columns - 1, itemCount - 1);

    if (direction === "first") {
        for (let i = firstInRow; i <= lastInRow; i++) {
            if (!isDisabledAt(i)) {
                return i;
            }
        }
    } else {
        for (let i = lastInRow; i >= firstInRow; i--) {
            if (!isDisabledAt(i)) {
                return i;
            }
        }
    }

    return -1;
};

/**
 * Find nearest enabled index when jumping (PageUp/PageDown)
 */
const findNearestEnabled = (
    targetIndex: number,
    currentIndex: number,
    itemCount: number,
    isDisabledAt: (index: number) => boolean,
    direction: 1 | -1,
): number => {
    let nextIndex = targetIndex;
    const isMovingForward = direction > 0;

    // First try: search from target in opposite direction back toward current
    const searchDirection = isMovingForward ? -1 : 1;
    while (
        nextIndex >= 0 &&
        nextIndex < itemCount &&
        isDisabledAt(nextIndex) &&
        (isMovingForward ? nextIndex <= currentIndex : nextIndex >= currentIndex)
    ) {
        nextIndex += searchDirection;
    }

    if (
        nextIndex >= 0 &&
        nextIndex < itemCount &&
        !isDisabledAt(nextIndex) &&
        (isMovingForward ? nextIndex > currentIndex : nextIndex < currentIndex)
    ) {
        return nextIndex;
    }

    // Second try: search from target in the direction of movement
    nextIndex = targetIndex;
    while (nextIndex >= 0 && nextIndex < itemCount && isDisabledAt(nextIndex)) {
        nextIndex += direction;
    }

    return nextIndex >= 0 && nextIndex < itemCount ? nextIndex : -1;
};

// Key Handlers

/**
 * Handle Up/Down arrow navigation (single-column only)
 */
const handleArrowUpDown = (
    e: React.KeyboardEvent,
    currentIndex: number,
    columns: number,
    itemCount: number,
    isDisabledAt: (index: number) => boolean,
    focusItem: (index: number) => void,
): boolean => {
    if (columns > 1) {
        return false; // Multi-column handled by 2D navigation
    }

    const direction = e.key === "ArrowDown" ? 1 : -1;
    const nextIndex = findNextEnabledIndex(currentIndex, direction, itemCount, isDisabledAt);

    if (nextIndex >= 0) {
        e.preventDefault();
        focusItem(nextIndex);
        return true;
    }

    return false;
};

/**
 * Handle Home/End keys (with Ctrl modifier for grid)
 */
const handleHomeEnd = (
    e: React.KeyboardEvent,
    currentIndex: number,
    columns: number,
    itemCount: number,
    isDisabledAt: (index: number) => boolean,
    focusItem: (index: number) => void,
): void => {
    e.preventDefault();

    const isHome = e.key === "Home";

    if (e.ctrlKey) {
        // Ctrl+Home/End: First/Last cell in entire grid
        const targetIndex = isHome
            ? findFirstEnabledIndex(itemCount, isDisabledAt)
            : findLastEnabledIndex(itemCount, isDisabledAt);

        if (targetIndex >= 0) {
            focusItem(targetIndex);
        }
    } else {
        // Home/End: First/Last cell in current row
        const targetIndex = findInRow(currentIndex, columns, itemCount, isDisabledAt, isHome ? "first" : "last");

        if (targetIndex >= 0) {
            focusItem(targetIndex);
        }
    }
};

/**
 * Handle PageUp/PageDown keys (jump by 10 items)
 */
const handlePageUpDown = (
    e: React.KeyboardEvent,
    currentIndex: number,
    itemCount: number,
    isDisabledAt: (index: number) => boolean,
    focusItem: (index: number) => void,
): void => {
    e.preventDefault();

    const isPageDown = e.key === "PageDown";
    const direction = isPageDown ? 1 : -1;
    const targetIndex = isPageDown ? Math.min(itemCount - 1, currentIndex + 10) : Math.max(0, currentIndex - 10);

    const nextIndex = findNearestEnabled(targetIndex, currentIndex, itemCount, isDisabledAt, direction);

    if (nextIndex >= 0) {
        focusItem(nextIndex);
    }
};

/**
 * Handle Space key (selection/activation)
 */
const handleSpace = (
    e: React.KeyboardEvent,
    currentIndex: number,
    hasSelection: boolean,
    isDisabledAt: (index: number) => boolean,
    getKeyByIndex: (index: number) => string,
    onActivate: (key: string) => void,
): void => {
    if (!hasSelection || isDisabledAt(currentIndex)) {
        return;
    }

    e.preventDefault();
    const key = getKeyByIndex(currentIndex);
    onActivate(key);
};

/**
 * Handle Enter/F2 (enter cell interaction mode)
 */
const handleInteractionMode = (
    e: React.KeyboardEvent,
    currentIndex: number,
    itemRefs: React.MutableRefObject<(HTMLElement | null)[]>,
    enterCellInteractionMode: (index: number) => void,
): void => {
    const currentElement = itemRefs.current[currentIndex];

    if (currentElement && hasInteractiveElements(currentElement)) {
        e.preventDefault();
        enterCellInteractionMode(currentIndex);
    }
};

// Main Hook

/**
 * Hook for keyboard navigation and selection in lists
 * Supports both single-column and multi-column layouts
 */
export const useSelection = ({
    itemCount,
    getKeyByIndex,
    onActivate,
    isDisabledAt = () => false,
    hasSelection,
    listId,
    columns = 1,
}: UseSelectionArgs) => {
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const itemRefs = useRef<(HTMLElement | null)[]>([]);

    // Cell interaction mode management
    const { cellMode, interactiveCellIndex, enterCellInteractionMode, exitCellInteractionMode } = useCellMode({
        itemCount,
        isDisabledAt,
    });

    const getItemRef = useCallback((index: number) => {
        return (el: HTMLElement | null) => {
            itemRefs.current[index] = el;
        };
    }, []);

    const focusItem = useCallback((index: number) => {
        const element = itemRefs.current[index];
        if (element) {
            element.focus();
            setFocusedIndex(index);
        }
    }, []);

    // Grid navigation integration
    const navigation2D = use2DNavigation({ itemCount, columns, focusItem, isDisabledAt });

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent, currentIndex: number) => {
            // Handle Escape - exit cell interaction mode
            if (e.key === "Escape" && cellMode === "interaction") {
                e.preventDefault();
                exitCellInteractionMode();
                focusItem(currentIndex);
                return;
            }

            // Handle Enter/F2 - enter cell interaction mode
            if ((e.key === "Enter" || e.key === "F2") && cellMode === "navigation") {
                handleInteractionMode(e, currentIndex, itemRefs, enterCellInteractionMode);
                return;
            }

            // In interaction mode, don't handle navigation keys (except Escape)
            if (cellMode === "interaction") {
                return;
            }

            // For multi-column lists, try 2D navigation first
            if (columns > 1 && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
                const handled = navigation2D.handleKeyDown(e, currentIndex);
                if (handled) {
                    return;
                }
            }

            // Handle single-column Up/Down navigation
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                handleArrowUpDown(e, currentIndex, columns, itemCount, isDisabledAt, focusItem);
                return;
            }

            // Handle Home/End
            if (e.key === "Home" || e.key === "End") {
                handleHomeEnd(e, currentIndex, columns, itemCount, isDisabledAt, focusItem);
                return;
            }

            // Handle PageUp/PageDown
            if (e.key === "PageUp" || e.key === "PageDown") {
                handlePageUpDown(e, currentIndex, itemCount, isDisabledAt, focusItem);
                return;
            }

            // Handle Space
            if (e.key === " ") {
                handleSpace(e, currentIndex, hasSelection, isDisabledAt, getKeyByIndex, onActivate);
                return;
            }

            // Tab exits the grid (default browser behavior)
        },
        [
            hasSelection,
            cellMode,
            exitCellInteractionMode,
            enterCellInteractionMode,
            focusItem,
            columns,
            navigation2D,
            isDisabledAt,
            getKeyByIndex,
            onActivate,
            itemCount,
        ],
    );

    const getItemProps = useCallback(
        (index: number) => {
            const firstEnabledIndex = findFirstEnabledIndex(itemCount, isDisabledAt);
            const isFirstEnabled = index === firstEnabledIndex;

            return {
                ref: getItemRef(index),
                tabIndex: isFirstEnabled && focusedIndex === -1 ? 0 : focusedIndex === index ? 0 : -1,
                onKeyDown: (e: React.KeyboardEvent) => handleKeyDown(e, index),
                onFocus: () => setFocusedIndex(index),
                id: `${listId}-option-${index}`,
            };
        },
        [focusedIndex, getItemRef, handleKeyDown, itemCount, isDisabledAt, listId],
    );

    return {
        focusedIndex,
        getItemProps,
        focusItem,
        activeDescendantId: focusedIndex >= 0 ? `${listId}-option-${focusedIndex}` : undefined,
        cellMode,
        interactiveCellIndex,
    };
};
